import { Call, RpcProvider } from 'starknet';
import { getConfigManager } from '../../config';
import { MULTICALL_CONTRACT_ADDRESS, POLLING_INTERVAL_IN_MS } from '../../constants';
import { Decimal } from '../../datastructures';
import { NostraToken, Nullable } from '../../interfaces';
import { getAccountAddressFromId } from '../../utils';
import { MultiCallContract, ContractRevertError } from '../../services';
import accountsAtom from './accountsAtom';
import nostraTokenListAtom from './nostraTokenListAtom';
import { walletAddressAtom } from '../wallet';
import { Atom, createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';
import { triggerImmediateRpcFailover } from '../rpcHealthAtom';

export interface NostraTokenBalanceMap {
  [accountTokenAddressId: string]: Nullable<Decimal>;
}

const fetchBalances = async (
  accountIds: number[],
  nostraTokens: NostraToken[],
  walletAddress: string,
  provider: RpcProvider,
) => {
  const accountIdNostraTokenPairs = accountIds.flatMap(accountId =>
    nostraTokens.map(nostraToken => [accountId, nostraToken] as const),
  );

  const calldata = accountIdNostraTokenPairs.map(
    ([accountId, nostraToken]) =>
      ({
        contractAddress: nostraToken.address,
        entrypoint: 'balanceOf',
        calldata: [getAccountAddressFromId(walletAddress, accountId)],
      } as Call),
  );

  const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

  const result = await multiCallContract.aggregateUint256ArrayAsync(calldata);

  if (!result) {
    throw new Error('Multicall returned null — RPC node may be unavailable');
  }

  return result
    .map((uint256Value, index) => {
      const [accountId, nostraToken] = accountIdNostraTokenPairs[index];
      const tokenPrecision = getConfigManager().getTokenPrecision(nostraToken.address);

      const balance = new Decimal(uint256Value, tokenPrecision);
      return {
        [`${walletAddress}-${accountId}-${nostraToken.address}`]: balance,
      } as NostraTokenBalanceMap;
    })
    .reduce((acc, cur) => ({ ...acc, ...cur }), {});
};

const nostraTokenBalancesAtom: Atom<NostraTokenBalanceMap> = createAsyncAtom<NostraTokenBalanceMap>(
  async get => {
    const accounts = await get(accountsAtom);
    const nostraTokenList = await get(nostraTokenListAtom);
    const walletAddress = await get(walletAddressAtom);
    const provider = await get(rpcProviderAtom);

    if (!walletAddress) {
      nostraTokenBalancesAtom.clearError();
      return {};
    }

    const activeAccountIds = accounts
      .map((account, accountId) => [account, accountId] as const)
      .filter(([account]) => account !== '')
      .map(([, accountId]) => accountId);

    try {
      const result = await fetchBalances(activeAccountIds, nostraTokenList, walletAddress, provider);
      nostraTokenBalancesAtom.clearError();
      return result;
    } catch (error) {
      console.error('Failed to load nostra token balances:', error);
      nostraTokenBalancesAtom.setError(error);
      if (!(error instanceof ContractRevertError)) {
        void triggerImmediateRpcFailover('Failed to load user balances');
      }
      return nostraTokenBalancesAtom.getResolvedValue() ?? {};
    }
  },
  POLLING_INTERVAL_IN_MS,
  [
    'lend-and-borrow-deposit',
    'lend-and-borrow-withdraw',
    'lend-and-borrow-borrow',
    'lend-and-borrow-repay',
    'lend-and-borrow-adjust',
    'unstake-and-deposit',
    'user-refresh',
  ],
);

export default nostraTokenBalancesAtom;
